import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight, Home, Archive, Search, Pin, Flame, X } from 'lucide-react';
import { ADMIN_SECTIONS, AdminSection, cardForPath } from '../../../config/adminNavCards';
import { useAdminNav } from '../../../contexts/AdminNavContext';
import { useAuth } from '../../../contexts/AuthContext';
import { AdminSidebarItem } from './AdminSidebarItem';
import { SECTION_ICONS } from './sectionIcons';

const OPEN_SECTIONS_KEY = 'adminNav:openSections';

function readOpenSections(): AdminSection[] {
  try {
    return JSON.parse(localStorage.getItem(OPEN_SECTIONS_KEY) || '[]') as AdminSection[];
  } catch {
    return [];
  }
}

/** CRM pages have their own sidebar — ours drops to the icon rail there. */
const isCrmPath = (p: string) => p === '/crm' || p.startsWith('/crm/') || p === '/crm-v2' || p.startsWith('/crm-v2/');

const GroupLabel: React.FC<{ collapsed: boolean; children: React.ReactNode }> = ({ collapsed, children }) =>
  collapsed ? (
    <div className="mx-2 my-2 border-t border-neutral-200" />
  ) : (
    <div className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">{children}</div>
  );

/**
 * The navigation itself — shared by the desktop sidebar and the mobile
 * drawer. Shows Pinned, Most used, then the sections (only cards that are
 * NOT rarely used), and a footer link into the Rarely Used group.
 */
const SidebarNav: React.FC<{ collapsed: boolean; onExpand?: () => void }> = ({ collapsed, onExpand }) => {
  const { activeCards, pinned, mostUsed, rarelyUsed, setPaletteOpen, isPinned, setMobileOpen } = useAdminNav();
  const { pathname } = useLocation();
  const [open, setOpen] = useState<AdminSection[]>(readOpenSections);

  // Keep the section of the current page open.
  const currentSection = useMemo(() => cardForPath(pathname, activeCards)?.section, [pathname, activeCards]);
  useEffect(() => {
    if (currentSection && !open.includes(currentSection)) setOpen((o) => [...o, currentSection]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection]);

  const toggleSection = (id: AdminSection) => {
    if (collapsed) {
      // In the icon rail a section icon expands the sidebar on that section.
      setOpen((o) => (o.includes(id) ? o : [...o, id]));
      onExpand?.();
      return;
    }
    setOpen((o) => {
      const next = o.includes(id) ? o.filter((x) => x !== id) : [...o, id];
      try {
        localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const mostUsedShown = mostUsed.filter((c) => !isPinned(c.id)).slice(0, 6);

  return (
    <nav className="flex-1 overflow-y-auto p-2">
      <button
        onClick={() => setPaletteOpen(true)}
        title="Search all tools (Ctrl/⌘ K)"
        className={`mb-1 flex w-full items-center gap-2 rounded-lg border border-neutral-200 px-2 py-1.5 text-sm text-neutral-500 hover:bg-neutral-50 ${
          collapsed ? 'justify-center' : ''
        }`}
      >
        <Search className="h-4 w-4 flex-shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">Search tools…</span>
            <kbd className="rounded border border-neutral-200 px-1 text-[10px] text-neutral-400">⌘K</kbd>
          </>
        )}
      </button>

      <AdminSidebarItem to="/dashboard" label="Dashboard" icon={Home} collapsed={collapsed} end />

      {pinned.length > 0 && (
        <>
          <GroupLabel collapsed={collapsed}>
            <span className="inline-flex items-center gap-1">
              <Pin className="h-3 w-3" /> Pinned
            </span>
          </GroupLabel>
          {pinned.map((c) => (
            <AdminSidebarItem key={c.id} to={c.path} label={c.title} icon={c.icon} color={c.color} collapsed={collapsed} />
          ))}
        </>
      )}

      {mostUsedShown.length > 0 && (
        <>
          <GroupLabel collapsed={collapsed}>
            <span className="inline-flex items-center gap-1">
              <Flame className="h-3 w-3" /> Most used
            </span>
          </GroupLabel>
          {mostUsedShown.map((c) => (
            <AdminSidebarItem key={c.id} to={c.path} label={c.title} icon={c.icon} color={c.color} collapsed={collapsed} />
          ))}
        </>
      )}

      <GroupLabel collapsed={collapsed}>Sections</GroupLabel>
      {ADMIN_SECTIONS.map((section) => {
        const items = activeCards.filter((c) => c.section === section.id);
        if (!items.length) return null;
        const isOpen = !collapsed && open.includes(section.id);
        const Icon = SECTION_ICONS[section.id];
        return (
          <div key={section.id}>
            <button
              onClick={() => toggleSection(section.id)}
              title={collapsed ? section.title : undefined}
              className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium text-neutral-800 hover:bg-neutral-100 ${
                collapsed ? 'justify-center' : ''
              } ${currentSection === section.id ? 'text-primary-700' : ''}`}
            >
              <Icon className="h-4 w-4 flex-shrink-0 text-neutral-500" />
              {!collapsed && (
                <>
                  <span className="flex-1 truncate text-left">{section.title}</span>
                  <span className="text-[11px] text-neutral-400">{items.length}</span>
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </>
              )}
            </button>
            {isOpen && (
              <div className="ml-3 border-l border-neutral-100 pl-1">
                {items.map((c) => (
                  <AdminSidebarItem key={c.id} to={c.path} label={c.title} icon={c.icon} color={c.color} collapsed={false} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {rarelyUsed.length > 0 && (
        <Link
          to="/dashboard#rarely-used"
          // A fresh state token re-opens the group even when already on that URL.
          state={{ openRarely: Date.now() }}
          onClick={() => setMobileOpen(false)}
          title={collapsed ? `Rarely Used (${rarelyUsed.length})` : undefined}
          className={`mt-3 flex items-center gap-2.5 rounded-lg border border-dashed border-neutral-300 px-2 py-1.5 text-sm text-neutral-500 hover:bg-neutral-50 ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <Archive className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span className="flex-1">Rarely Used ({rarelyUsed.length})</span>}
        </Link>
      )}
    </nav>
  );
};

/**
 * Left navigation for the HQ/admin roles: a sticky desktop sidebar that
 * collapses to an icon rail (remembered per user), and a slide-out drawer
 * on mobile opened from the top-bar hamburger.
 */
export const AdminSidebar: React.FC<{ mobileExtras?: React.ReactNode }> = ({ mobileExtras }) => {
  const { enabled, sidebarCollapsed, setSidebarCollapsed, mobileOpen, setMobileOpen } = useAdminNav();
  const { isImpersonating } = useAuth();
  const { pathname } = useLocation();
  // On CRM pages default to the rail; a click expands it just for this visit.
  const onCrm = isCrmPath(pathname);
  const [crmExpanded, setCrmExpanded] = useState(false);
  useEffect(() => setCrmExpanded(false), [onCrm]);

  if (!enabled) return null;
  const collapsed = onCrm ? !crmExpanded : sidebarCollapsed;
  const toggle = () => (onCrm ? setCrmExpanded((v) => !v) : setSidebarCollapsed(!sidebarCollapsed));
  const expand = () => (onCrm ? setCrmExpanded(true) : setSidebarCollapsed(false));

  return (
    <>
      {/* Desktop */}
      <aside
        className={`sticky top-16 hidden flex-shrink-0 flex-col border-r border-neutral-200 bg-white transition-[width] duration-200 md:flex ${
          collapsed ? 'w-16' : 'w-64'
        }`}
        // Fits under the 64px top bar — and, while impersonating, also under the
        // 60px impersonation banner the top bar is pushed below, so the Collapse
        // button never sits below the fold.
        style={{ zIndex: 30, height: isImpersonating ? 'calc(100vh - 124px)' : 'calc(100vh - 64px)' }}
        aria-label="Admin navigation"
      >
        <SidebarNav collapsed={collapsed} onExpand={expand} />
        <button
          onClick={toggle}
          className="flex h-10 items-center justify-center gap-2 border-t border-neutral-200 text-xs text-neutral-500 hover:bg-neutral-50"
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : (
            <>
              <ChevronsLeft className="h-4 w-4" /> Collapse
            </>
          )}
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        // Above the top nav (10001), below modals (10002+).
        <div className="fixed inset-0 md:hidden" style={{ zIndex: 10001 }}>
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-72 max-w-[85vw] flex-col bg-white shadow-xl">
            <div className="flex h-14 items-center justify-between border-b border-neutral-200 px-3">
              <span className="text-sm font-semibold text-neutral-900">Menu</span>
              <button onClick={() => setMobileOpen(false)} className="rounded-md p-1.5 hover:bg-neutral-100" title="Close">
                <X className="h-5 w-5 text-neutral-600" />
              </button>
            </div>
            <SidebarNav collapsed={false} />
            {mobileExtras && <div className="border-t border-neutral-200 p-2">{mobileExtras}</div>}
          </aside>
        </div>
      )}
    </>
  );
};

export default AdminSidebar;
